import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowRight, Truck, Wrench, Tractor, Package, 
  Shield, DollarSign, CheckCircle, Clock
} from 'lucide-react';

const EquipmentFinancingPage = () => {
  const equipmentTypes = [
    { 
      icon: Tractor, 
      title: "Commercial Mowers", 
      description: "Zero-turn mowers, stand-on mowers, walk-behinds, and riding mowers" 
    },
    { 
      icon: Truck, 
      title: "Trucks & Trailers", 
      description: "Work trucks, enclosed trailers, open trailers, and utility vehicles" 
    },
    { 
      icon: Wrench, 
      title: "Specialty Equipment", 
      description: "Aerators, dethatchers, sprayers, and turf management equipment" 
    },
    { 
      icon: Package, 
      title: "Tools & Attachments", 
      description: "Blowers, trimmers, edgers, chainsaws, and equipment attachments" 
    },
  ];

  const benefits = [
    "Finance new or used equipment",
    "Preserve working capital for operations",
    "Predictable monthly payments",
    "Potential tax advantages (consult your accountant)",
    "Keep up with technology and efficiency improvements",
    "Flexible terms to match equipment lifespan"
  ];

  const financingOptions = [
    {
      title: "Equipment Loans",
      description: "Purchase equipment outright with structured financing. You own the equipment from day one.",
      features: ["Full ownership", "Fixed payments", "Build equity"]
    },
    {
      title: "Equipment Leasing",
      description: "Use the equipment you need without the full purchase commitment. Options to buy at end of term.",
      features: ["Lower monthly payments", "Upgrade flexibility", "Potential tax benefits"]
    }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Truck className="h-4 w-4" />
                Equipment Financing
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Equipment Financing for Landscaping Companies
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Finance commercial mowers, trucks, trailers, and all the equipment your landscaping business needs to operate and grow—without depleting your working capital.
              </p>
              <Link to="/apply">
                <Button size="lg" className="bg-primary hover:bg-primary/90">
                  Apply Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <Tractor className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Equipment Financing</h3>
                  <p className="text-gray-500">Get the tools you need to grow</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Equipment Value</span>
                    <span className="text-xl font-bold text-gray-900">$45,000</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Down Payment</span>
                  <span className="text-lg font-semibold text-gray-900">10-20%</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Term</span>
                  <span className="text-lg font-semibold text-gray-900">24-72 Months</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                  <span className="text-amber-700">Est. Monthly Payment</span>
                  <span className="text-xl font-bold text-amber-700">From $650/mo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Equipment Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Finance the Equipment You Need</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From mowers to trucks, we can help you finance the essential equipment that keeps your landscaping business running.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {equipmentTypes.map((equipment, index) => (
              <Card key={index} className="border-gray-100 text-center">
                <CardContent className="pt-8 pb-8">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <equipment.icon className="h-7 w-7 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{equipment.title}</h3>
                  <p className="text-gray-600 text-sm">{equipment.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credit Types Available */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">All Credit Types Welcome</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We work with businesses across the credit spectrum. Whether you have excellent credit or are still building, we have financing solutions for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="border-gray-100 bg-emerald-50 border-emerald-200">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Excellent Credit</h3>
                <p className="text-sm text-gray-600">720+ FICO</p>
                <p className="text-xs text-emerald-600 mt-2">Best rates available</p>
              </CardContent>
            </Card>
            <Card className="border-gray-100 bg-blue-50 border-blue-200">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Good Credit</h3>
                <p className="text-sm text-gray-600">680-719 FICO</p>
                <p className="text-xs text-blue-600 mt-2">Competitive rates</p>
              </CardContent>
            </Card>
            <Card className="border-gray-100 bg-amber-50 border-amber-200">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Fair Credit</h3>
                <p className="text-sm text-gray-600">620-679 FICO</p>
                <p className="text-xs text-amber-600 mt-2">Options available</p>
              </CardContent>
            </Card>
            <Card className="border-gray-100 bg-orange-50 border-orange-200">
              <CardContent className="pt-6 pb-6 text-center">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Challenged Credit</h3>
                <p className="text-sm text-gray-600">Below 620 FICO</p>
                <p className="text-xs text-orange-600 mt-2">We can help</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">We Also Consider:</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="font-medium text-gray-900 mb-1">Startups</div>
                <p className="text-sm text-gray-600">New businesses with limited credit history</p>
              </div>
              <div className="text-center">
                <div className="font-medium text-gray-900 mb-1">Past Bankruptcy</div>
                <p className="text-sm text-gray-600">Discharged bankruptcies considered</p>
              </div>
              <div className="text-center">
                <div className="font-medium text-gray-900 mb-1">Tax Liens</div>
                <p className="text-sm text-gray-600">Resolved tax situations welcome</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financing Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Financing Structures</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the financing structure that works best for your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {financingOptions.map((option, index) => (
              <Card key={index} className="border-gray-100">
                <CardContent className="pt-8 pb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{option.title}</h3>
                  <p className="text-gray-600 mb-6">{option.description}</p>
                  <ul className="space-y-2">
                    {option.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-amber-600" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Why Finance Your Equipment?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Equipment financing allows you to acquire the tools you need while preserving cash flow for daily operations. Spread the cost over time and let your equipment pay for itself.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Equipment We Finance</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Zero-turn mowers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Stand-on mowers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Walk-behind mowers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Work trucks</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Enclosed trailers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Open trailers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Aerators</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Sprayers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Skid steers</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">Mini excavators</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple Financing Process</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Choose Equipment</h3>
              <p className="text-gray-600 text-sm">Select the equipment you need from any vendor or dealer</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Apply</h3>
              <p className="text-gray-600 text-sm">Submit your application with equipment details</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Approved</h3>
              <p className="text-gray-600 text-sm">Receive financing terms and approval</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Your Equipment</h3>
              <p className="text-gray-600 text-sm">We pay the vendor, you get your equipment</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Upgrade Your Equipment?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Apply for equipment financing and get the tools your landscaping business needs to succeed.
          </p>
          <Link to="/apply">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Apply Now <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default EquipmentFinancingPage;
